import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { BaseFilterCollapseService, ConfigService, MessageService } from '../../../../core';
import { EnumUsuarioPerfil } from '../../../../domains/shared/components/usuario-multi-select/enums/usuario-perfil.enum';
import { EnumSituacao } from '../../../../domains/shared/enums/situacao.enum';
import { Cidade } from '../../../cidade';
import { ConfigEmpresa } from '../../../configuracoes/app/empresa';
import { RepresentanteFilter } from './representante-filter';
import { RepresentanteFilterStorageService } from './representante-filter-storage.service';

@Component({
    selector: 'sgt-representante-filter',
    templateUrl: 'representante-filter.component.html',
    styleUrls: ['./representante-filter.component.less']
})
export class RepresentanteFilterComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    readonly EnumUsuarioPerfil = EnumUsuarioPerfil;
    readonly EnumSituacao = EnumSituacao;

    configEmpresa: ConfigEmpresa;

    // Filter
    @Input() filter: RepresentanteFilter;
    isFilterInitializing = false;
    isFilterInitialized = false;
    @Input() allowStorage = true;
    @Output() filterInitialized = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterCleaned = new EventEmitter<any>();

    // Valores padrões
    @Input() situacao: number;

    // Filrer pesquisa
    filterPsqSubscription: Subscription;
    isFirstChangeFilterPsq = true;
    @ViewChild('filterPsq', {static: true}) filterPsq: NgModel;

    // Filter collapse
    @Input() isCollapsedInDefault = false;
    isCollapsedIn = false;
    @ViewChild('filterCollapse') filterCollapse: ElementRef;
    collapseIcon = 'fas fa-caret-down';

    // Multiselects
    isFirstChangeClassificacoesClientesMultiselect = true;

    constructor(private baseFilterCollapseService: BaseFilterCollapseService,
                private configService: ConfigService,
                private messageService: MessageService,
                private representanteFilterStorageService: RepresentanteFilterStorageService,
                private cdr: ChangeDetectorRef) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();

        // Aplicando o filtro salvo no storage
        if (this.allowStorage) {
            this.representanteFilterStorageService.applyFilter(this.filter);
        }
        // Iniciando filtro
        this.initFilter();
    }

    ngAfterViewInit() {
        if (!this.isCollapsedInDefault) {
            jQuery(this.filterCollapse.nativeElement).css('display', 'none');
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'isCollapsedInDefault') {
                if (changes[propName].isFirstChange()) {
                    setTimeout(() => {
                        this.isCollapsedIn = changes[propName].currentValue;
                    }, 100);
                }
            }
        }
    }

    /** Inicializa o filtro */
    private initFilter() {
        this.isFilterInitializing = true;
        this.isFilterInitialized = false;

        // Filter de pesquisa
        this.subscribeToFilterPsq();

        setTimeout(() => {
            this.isFilterInitializing = false;
            this.isFilterInitialized = true;
            this.filterInitialized.emit();
        }, 0);
    }

    // Realiza o subscribe nas alterações feitas no input do filtro da pesquisa
    private subscribeToFilterPsq() {
        this.filterPsqSubscription = this.filterPsq.valueChanges
            .pipe(
                debounceTime(1000), // Dando um debounce de 1000ms para emitir a alteração
                map(emittedValue => emittedValue?.trim()),
                distinctUntilChanged()
            )
            .subscribe(() => {
                if (this.isFirstChangeFilterPsq) {
                    this.isFirstChangeFilterPsq = false;
                    return;
                }

                if (this.filter.filterPsq?.trim().length > 0) {
                    this.filter.isFilterDirty = true;
                    this.cdr.detectChanges();
                }

                this.filterChange.emit();
            });
    }

    /** Emite evento de alteração no filtro para atualizar a pesquisa */
    refresh() {
        this.close();
        this.filterChange.emit();
    }

    /** Limpa o filtro e atualiza a pesquisa */
    clear() {
        // Removendo os filtros
        this.baseFilterCollapseService.clear(this);
        this.filter.situacoes = [this.situacao]; // Setando a situação padrão (se hover uma)

        this.close();
        this.filterCleaned.emit();
    }

    /** Abre/Fecha o filtro */
    toggleCollapse() {
        this.baseFilterCollapseService.toggleCollapse(this);
    }

    /** Abre o filtro */
    open() {
        this.baseFilterCollapseService.open(this);
    }

    /** Fecha o filtro */
    close() {
        this.baseFilterCollapseService.close(this);
    }

    hideSituacoes(): boolean {
        return this.situacao != null && this.situacao !== undefined;
    }

    /** Ao alterar as situações */
    onChangeSituacoes() {
        this.filterChange.emit();
    }

    /** Ao alterar as modalidades comerciais */
    onChangeModalidadesComerciais() {
        this.filterChange.emit();
    }

    /** Ao alterar as classificações de clientes */
    onChangeClassificacoesClientes() {
        if (this.isFirstChangeClassificacoesClientesMultiselect) {
            this.isFirstChangeClassificacoesClientesMultiselect = false;
            return;
        }

        this.filterChange.emit();
    }


    /** Ao alterar os atendentes */
    onChangeAtendentes() {
        this.filterChange.emit();
    }

    /** Ao alterar os gerentes */
    onChangeGerentes() {
        this.filterChange.emit();
    }

    /** Ao alterar a data de cadastro inicial/final */
    onChangeDataCadastro(date: Date) {
        if (date) {
            this.filter.isFilterDirty = true;
            this.cdr.detectChanges();
        }
    }

    /** Ao alterar os estados */
    onChangeEstados() {
        this.filter.isFilterDirty = true;
        this.cdr.detectChanges();
    }

    /** Ao alterar a cidade */
    onChangeCidade(cidade: Cidade) {
        if (cidade) {
            this.filter.idCidade = cidade.id;
            this.filter.isFilterDirty = true;
            this.cdr.detectChanges();
        } else {
            this.filter.idCidade = null;
        }
    }

    ngOnDestroy() {
        this.filterPsqSubscription?.unsubscribe();
    }
}
